import { loadSalesPreferences } from "../qv4/salesPreferences";
import { notificationDesign } from '../../utils/notificationDesign';
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { notification as staticNotification, Button } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useWatiSocket } from "./WatiSocketContext";
import { useSocket } from "./SocketContext";
import { useAuth } from "./AuthContext";
import { fetchChannelAssignments, mapChatItem, matchesChatFilters, effectiveAssigneeIdsFilter, isFavouritesFilterTab } from "../qv4/helpers";
import { useWatiChatList } from "../qv4/useWatiChatList";
import { setOnWatiAuthError, setWatiAuthBlocked } from "../axiosSetup";
import watiIncomingSound from "../../assets/sounds/wati_incoming_sound.mp3";

const SEND_ENDPOINT = "https://srm.uzdpart.com:33123/whatsapp/send_wati_general_message";
const QV4_CHAT_API = "https://srm.uzdpart.com:33123/wati_messages/fetch_messages_v2";

// Rendered inside Ant Design's notification portal — must not use any React context hooks.
// All data is passed as plain props captured at notification creation time.
const QuickReplyBox = ({ phone, userName, notificationKey, onNavigate, showCloseAll, onCloseAll, channelId, notification }) => {
    const [text, setText] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const handleSend = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await axios.post(SEND_ENDPOINT, {
                phone,
                message: text.trim(),
                user: userName,
                channelId,
            });
            setSent(true);
            setTimeout(() => notification.destroy(notificationKey), 800);
        } catch {
            setSending(false);
        }
    };

    return (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 6 }}>
            {sent ? (
                <div style={{ textAlign: "center", color: "#52c41a", fontWeight: 600, padding: "2px 0", fontSize: 12 }}>
                    ✓ Sent!
                </div>
            ) : (
                <>
                    <textarea
                        value={text}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="Quick reply… (Enter to send)"
                        rows={1}
                        style={{
                            width: "100%",
                            resize: "none",
                            borderRadius: 5,
                            border: "1px solid #d9d9d9",
                            padding: "3px 7px",
                            fontSize: 11,
                            outline: "none",
                            boxSizing: "border-box",
                        }}
                    />
                    <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
                        <button
                            onClick={handleSend}
                            disabled={!text.trim() || sending}
                            style={{
                                flex: 1,
                                backgroundColor: !text.trim() || sending ? "#d1d1d1" : "#075e54",
                                color: "white",
                                border: "none",
                                borderRadius: 5,
                                padding: "4px 6px",
                                cursor: !text.trim() || sending ? "not-allowed" : "pointer",
                                fontSize: 11,
                                fontWeight: 600,
                            }}
                        >
                            {sending ? "Sending…" : "Send ↵"}
                        </button>
                        <button
                            onClick={onNavigate}
                            style={{
                                backgroundColor: "transparent",
                                color: "#1677ff",
                                border: "1px solid #1677ff",
                                borderRadius: 5,
                                padding: "4px 8px",
                                cursor: "pointer",
                                fontSize: 11,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Go to chat →
                        </button>
                    </div>
                    {showCloseAll && (
                        <button
                            onClick={onCloseAll}
                            style={{
                                width: "100%",
                                marginTop: 3,
                                backgroundColor: "transparent",
                                color: "#aaa",
                                border: "none",
                                padding: "2px 0",
                                cursor: "pointer",
                                fontSize: 10,
                            }}
                        >
                            Dismiss all
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

const WatiChatContext = createContext();

export const useWatiChat = () => {
    const context = useContext(WatiChatContext);
    if (!context) {
        throw new Error("useWatiChat must be used within a WatiChatProvider");
    }
    return context;
};

export const WatiChatProvider = ({ children }) => {
    // The actual root cause of the long-standing "notifications land in the
    // wrong corner" bug: "topCenter" was never a valid antd NotificationPlacement
    // (valid values are top/topLeft/topRight/bottom/bottomLeft/bottomRight —
    // see node_modules/antd/es/notification/interface.d.ts). Every call across
    // this codebase used "topCenter"; antd string-templated it straight into
    // the container's class name (so it always *looked* right in devtools)
    // but had no matching entry in its placement->position lookup, silently
    // falling through to a broken position. Fixed by using "top" everywhere
    // (see the placement values below and in ChatPanelMessages/QuerysetV4/
    // CustomerEditDrawer/useWatiChatList).
    //
    // These two isolated instances (kept even after finding the real cause,
    // since they're still good practice) mean the chat/QV4 subsystem's
    // notifications never share an antd notification instance — and
    // therefore never share internal position/stacking state — with the
    // app-wide shared instance (useSnackbar's bottomLeft default,
    // other feature-specific placements (for example Operations' topRight)
    // or with the
    // incoming-message alert's own separate, user-configurable placement.
    // stack: false must be passed explicitly to each — it does NOT inherit
    // from <AntApp notification={{ stack: false }}>, which only configures
    // App's own default instance.
    const [chatNotifApi, chatNotifContextHolder] = staticNotification.useNotification({ stack: false });
    const [incomingNotifApi, incomingNotifContextHolder] = staticNotification.useNotification({ stack: false });
    const { watiChatUpdates, isWatiConnected } = useWatiSocket();
    const { socket } = useSocket();
    const { authState } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // A chunked media upload can fail well after the agent has navigated
    // away from that chat (or even closed the chat drawer entirely) — this
    // provider is always mounted, so it's the one place that can reliably
    // surface that failure regardless of what's currently on screen. The
    // per-chat progress bars/bubble status in ChatPanelMessages handle the
    // "still looking at this chat" case separately; this is the fallback.
    useEffect(() => {
        if (!socket) return;
        const handleMediaUploadFailed = (payload) => {
            const data = payload?.data;
            if (!data || data.status !== "failed") return;
            chatNotifApi.error({
      ...notificationDesign('customer', 'error'),
                message: "A media message failed to send",
                description: `Customer: ${data.whatsappId || "unknown number"} — ${data.error || "Upload to WhatsApp failed."}`,
                placement: "top",
                duration: 10,
            });
        };
        socket.on("wati:media_upload_progress", handleMediaUploadFailed);
        return () => socket.off("wati:media_upload_progress", handleMediaUploadFailed);
    }, [socket, chatNotifApi]);

    // Supplier-channel preload — feeds the "WhatsApp Messages" nav badge and
    // seeds WhatsappMessages' own chat list. Channel resolved from uzd_misc
    // mode 'wati_channel_assignments' rather than hardcoded, since WATI
    // supports up to 4 channels and which one is "the supplier channel" is a
    // config decision, not a frontend constant.
    const [supplierChannelId, setSupplierChannelId] = useState(null);
    const [chatlist, setChatlist] = useState([]);
    const [chatlistLoading, setChatlistLoading] = useState(true);
    const [continuationToken, setContinuationToken] = useState(null);
    const [chatlistCount, setChatlistCount] = useState(null);
    const [qv4Chatlist, setQv4Chatlist] = useState([]);
    const [qv4ChatsLoading, setQv4ChatsLoading] = useState(false);
    // The preload's own pagination cursor — previously discarded, which meant
    // whichever qv4 page seeded its chat list from this cache had no way to
    // page beyond it: "Load More" always re-requested page 1.
    const [qv4ChatlistToken, setQv4ChatlistToken] = useState(null);
    // Same story for the total-count badge — also discarded, so it never
    // showed anything when the page loaded from this cache (the usual case).
    const [qv4ChatlistCount, setQv4ChatlistCount] = useState(null);

    // Also mirrored into a ref so the socket handler below (which only
    // re-runs when watiChatUpdates changes) always reads the latest value
    // instead of whatever it closed over at its last run.
    const supplierChannelIdRef = useRef(null);
    useEffect(() => {
        fetchChannelAssignments().then(a => {
            setSupplierChannelId(a.supplier_support || null);
            supplierChannelIdRef.current = a.supplier_support || null;
        });
    }, []);

    // ── Shared chat panel state (Phase 2) ───────────────────────────────────
    // Lives here, not in a per-page hook, so QuerysetV4 and Operations see
    // the exact same chat list/filter/search/pagination regardless of which
    // is currently mounted — switching pages used to silently reset the
    // filter to "All Chats" and drop any pages loaded past the initial ~100.
    const {
        activeChats, setActiveChats,
        chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
        chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
        chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
        assigneeIdsFilter, onSelectAgentFilter, commitAgentFilter,
        statusesFilter, onSelectStatusFilter, commitStatusFilter, clearSidebarFilters,
        unassignedFilter, setUnassignedFilter,
        dateRangeFilter, setDateRangeFilter, dateScanStatus, continueDateScan, cancelDateScan,
        setChatContinuationToken, chatContinuationTokenRef,
        buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter,
        focusedChatIds, focusedLoading, focusedProgress, loadFocusedChats, exitFocusedChatMode,
    } = useWatiChatList({
        authState,
        persistKey: "customer",
    });

    // Separate, channel-scoped chat list/cache for the supplier side
    // (WhatsappMessages) — same fetch/filter/pagination logic via the shared
    // hook, deliberately its own state. Lives here (not inside WhatsappMessages
    // itself) for the same reason the customer list above does: so the socket
    // sync effect below can patch it directly, and so supplierChannelId (also
    // resolved in this component) and this hook's channelIds are always
    // updated in the same render — no cross-component prop-timing race like
    // the one that used to cause the supplier/customer chat lists to swap on
    // launch (channelIds used to arrive at WhatsappMessages a render late via
    // a separate prop hookup).
    const supplierChat = useWatiChatList({
        authState,
        channelIds: supplierChannelId ? [supplierChannelId] : undefined,
        seed: { list: chatlist, loading: chatlistLoading, token: continuationToken, count: chatlistCount },
        // The admin's Chat Settings (allowed filters / default filter /
        // admin exemption) are scoped to the customer channel only — the
        // supplier chat list here stays on the plain WATI-default behavior.
        applyChatSettings: false,
        // supplierChannelId resolves asynchronously (starts null) — don't let
        // the hook fetch/seed-check at all until it's actually known.
        requireChannelIds: true,
        persistKey: "supplier",
    });

    const watiProcessedCountRef = useRef(0);
    // WhatsappMessages writes the currently open chat ID here so the
    // socket handler can keep unreadCount at 0 for that chat.
    const activeChatIdRef = useRef(null);
    // QuerysetV4 and Operations each write the currently open chat ID here
    // (whichever of them is mounted) so the socket handler can do the same
    // for the customer channel's active list.
    const customerActiveChatIdRef = useRef(null);
    // QuerysetV4/Operations register a "select this chat" function here
    // (whichever is mounted) so a notification's "Go to chat" button can open
    // a customer-channel chat directly without a location.state round-trip —
    // there's no dedicated customer-chat route to navigate to the way
    // WhatsappMessages has for the supplier channel, so this is the only way
    // to reach into a currently-mounted customer page's own selection state.
    const customerOpenChatRef = useRef(null);
    const notificationAudioRef = useRef(new Audio(watiIncomingSound));
    const activeNotifsRef = useRef(new Set());
    const wasDownRef = useRef(false);
    // isWatiConnected starts false at mount (before the socket has ever
    // connected) — without this guard, the effect below reads that initial
    // false as "went down" and treats the very first successful connection
    // as a *recovery* from an outage, firing a redundant unfiltered refetch
    // (and "Chat list refreshed" notification) that raced with and
    // overwrote the customer channel's correctly-resolved initial filtered
    // fetch. Only count it as "was down" once a connection has actually
    // been established at least once before.
    const hasEverConnectedRef = useRef(false);

    const getAllChats = async () => {
        if (!supplierChannelId) return;
        setChatlistLoading(true);
        try {
            const res = await axios.post(
                QV4_CHAT_API,
                { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null, channelIds: [supplierChannelId] },
                { headers: { accessToken: localStorage.getItem("accessToken") } }
            );
            if (res.data?.success) {
                setChatlist(res.data.data?.final_data || []);
                setContinuationToken(res.data.data?.continuationToken || null);
                setChatlistCount(res.data.data?.count ?? null);
            }
        } catch (_) {
            // silent — WhatsappMessages shows error toasts for user-triggered reloads
        } finally {
            setChatlistLoading(false);
        }
    };

    // Load chatlist globally once the user is authenticated and the supplier
    // channel has been resolved from uzd_misc.
    useEffect(() => {
        if (!authState?.isAuthenticated || !supplierChannelId) return;
        getAllChats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState?.isAuthenticated, supplierChannelId]);

    // Load QV4 chatlist (channelIds: "") — used for unread badge AND to seed QuerysetV4 on open
    useEffect(() => {
        const load = async () => {
            setQv4ChatsLoading(true);
            try {
                const res = await axios.post(
                    QV4_CHAT_API,
                    { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null },
                    { headers: { accessToken: localStorage.getItem("accessToken") } }
                );
                if (res.data?.success) {
                    setQv4Chatlist(res.data.data?.final_data || []);
                    setQv4ChatlistToken(res.data.data?.continuationToken || null);
                    setQv4ChatlistCount(res.data.data?.count ?? null);
                }
            } catch (_) {}
            finally { setQv4ChatsLoading(false); }
        };
        if (authState?.id) load();
    }, [authState?.id]);

    // Single global source of "reload everything after a reconnect" — no
    // grace period, since even a brief outage can mean a missed update and
    // the cost of an extra refetch is far lower than the cost of silently
    // dropping a chat. Previously WhatsappMessages (supplier page) also ran
    // its own copy of this same "was it down" check, refetching its own
    // displayed list and popping its own "Socket reconnected" toast — two
    // independent handlers racing against each other (and each carrying the
    // same first-connect false-positive this effect just fixed) was the
    // actual cause of the supplier/customer chat lists briefly swapping at
    // site launch. Now there is exactly one handler here, covering both
    // channels' background badge/preload lists AND both channels' actual
    // displayed lists (supplierChat now lives here too, so it's reset
    // directly rather than via a signal). `reconnectSignal` remains so
    // WhatsappMessages can still reload the currently-open chat's own
    // messages, which is page-local state this context has no view into.
    const [reconnectSignal, setReconnectSignal] = useState(0);
    useEffect(() => {
        if (!isWatiConnected) {
            if (hasEverConnectedRef.current) {
                wasDownRef.current = true;
            }
        } else {
            hasEverConnectedRef.current = true;
            if (wasDownRef.current) {
                wasDownRef.current = false;
                // Reload the background badge/preload lists (always "All Chats",
                // since nothing viewing them has a filter selection of its own),
                // the customer channel's actual shared, currently-rendered list
                // (re-uses whatever filter/search is currently selected, since
                // fetchActiveChats defaults to the live filter/search state
                // rather than resetting it), and the supplier channel's actual
                // list (reset to its default "All Chats" filter, matching what
                // WhatsappMessages did locally before this was centralized).
                Promise.all([
                    getAllChats(),
                    authState?.id ? axios.post(
                        QV4_CHAT_API,
                        { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null },
                        { headers: { accessToken: localStorage.getItem("accessToken") } }
                    ).then(res => {
                        if (res.data?.success) {
                            setQv4Chatlist(res.data.data?.final_data || []);
                            setQv4ChatlistToken(res.data.data?.continuationToken || null);
                            setQv4ChatlistCount(res.data.data?.count ?? null);
                        }
                    }).catch(() => {}) : Promise.resolve(),
                    fetchActiveChats(),
                    supplierChat.resetToDefaultFilter(),
                ]).then(() => {
                    setReconnectSignal(s => s + 1);
                    chatNotifApi.info({
      ...notificationDesign('customer', 'info'),
                        message: "Chat & board data refreshed",
                        description: "WhatsApp reconnected after a brief outage — chat data and the Kanban/task boards have been reloaded to catch up on missed updates.",
                        placement: "top",
                        duration: 6,
                    });
                });
            }
        }
    }, [isWatiConnected]);

    // ── WATI auth broken (revoked refresh token) ────────────────────────────
    // Set once the backend relays a real HTTP 401 from any
    // /wati_messages/fetch* endpoint (detected centrally in axiosSetup.js,
    // which has no React access — this is where the actual reaction lives).
    // A routine expired token never reaches this state at all: it's
    // refreshed proactively server-side well before it lapses. A 401 here
    // specifically means the agent's WATI *refresh* token itself has been
    // revoked and needs a human to re-authenticate that connection — there
    // is nothing to auto-retry. Setting this both blocks further chat
    // fetches at the request-interceptor level (axiosSetup.js) and freezes
    // the WATI socket update processor below, so the UI doesn't keep
    // half-applying live updates it can no longer back up with a real fetch.
    const [watiAuthBroken, setWatiAuthBroken] = useState(false);
    const [watiAuthErrorMessage, setWatiAuthErrorMessage] = useState(null);

    useEffect(() => {
        setOnWatiAuthError((msg) => {
            setWatiAuthBroken(true);
            setWatiAuthErrorMessage(msg);
        });
        return () => setOnWatiAuthError(null);
    }, []);

    useEffect(() => {
        setWatiAuthBlocked(watiAuthBroken);
    }, [watiAuthBroken]);

    // Manual recovery action on the persistent notification below — unblocks
    // the request interceptor first so this retry can actually reach the
    // network, then re-runs the same reload this context already does after
    // a socket reconnect. If it's still broken, the same 401 handler fires
    // again on its own; nothing special needs to happen here for that case.
    const retryWatiAuth = () => {
        chatNotifApi.destroy("wati-auth-broken");
        setWatiAuthBroken(false);
        Promise.all([
            getAllChats(),
            authState?.id ? axios.post(
                QV4_CHAT_API,
                { userId: authState?.id, continuationToken: "", filterType: 0, subConditions: null, searchString: null, searchOptionType: null },
                { headers: { accessToken: localStorage.getItem("accessToken") } }
            ).then(res => {
                if (res.data?.success) {
                    setQv4Chatlist(res.data.data?.final_data || []);
                    setQv4ChatlistToken(res.data.data?.continuationToken || null);
                    setQv4ChatlistCount(res.data.data?.count ?? null);
                }
            }).catch(() => {}) : Promise.resolve(),
            fetchActiveChats(),
            supplierChat.resetToDefaultFilter(),
        ]).catch(() => {});
    };

    useEffect(() => {
        if (!watiAuthBroken) return;
        chatNotifApi.error({
      ...notificationDesign('customer', 'error'),
            key: "wati-auth-broken",
            message: "WhatsApp connection needs to be reconnected",
            description: `${watiAuthErrorMessage || "WATI accessToken issue, Contact Admin"} — chat fetching is paused until this is fixed.`,
            placement: "top",
            duration: 0,
            btn: <Button size="small" type="primary" onClick={retryWatiAuth}>Retry</Button>,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watiAuthBroken, watiAuthErrorMessage]);

    // Single canonical processor for the raw WATI socket event stream —
    // covers BOTH channels' background badge/preload lists (chatlist,
    // qv4Chatlist) AND both channels' actual displayed lists (activeChats,
    // supplierChat.activeChats), so any page showing either channel's chats
    // sees the same live data regardless of which page happens to be
    // mounted. This replaces three previously-separate, drifted copies of
    // this same logic that used to live in QuerysetV4.js, Operations.js, and
    // WhatsappMessages.js — each patching only its own page-local list, with
    // Operations' copy missing several branches (ticket reassignment,
    // broadcast messages) that QuerysetV4's had. Pages keep only the
    // reactions that are genuinely page-local and can't live here: which
    // chat is currently open in that page's own drawer (flash animation,
    // syncing the open `selectedChat` object, new-customer enrichment).
    useEffect(() => {
        // Frozen while the WATI token is broken — see the block above. Still
        // advances the processed-count marker so the backlog that piles up
        // while broken doesn't get replayed all at once once it's fixed;
        // recovery (retryWatiAuth) does a full fetch instead, which is more
        // trustworthy than a stale incremental delta anyway.
        if (watiAuthBroken) { watiProcessedCountRef.current = watiChatUpdates.length; return; }
        if (watiChatUpdates.length <= watiProcessedCountRef.current) return;

        const newUpdates = watiChatUpdates.slice(watiProcessedCountRef.current);
        watiProcessedCountRef.current = watiChatUpdates.length;

        // Shared upsert-and-resort used by every branch below that patches an
        // already-mapChatItem-shaped list (activeChats / supplierChat.activeChats).
        // `conv.type` on live socket payloads is WATI's *message* type
        // ("text"/"image"/etc), not our own customer classification (which only
        // the REST fetch's backend-side enrichment provides) — never trust it
        // here, always keep whatever the card already had. Same idea for `tags`
        // (rarely present live) and `listNotes` as a fallback. isBlocked gets the
        // same treatment: a plain message event's payload may simply omit it
        // (mapChatItem then coerces that to false), which would silently
        // un-flag an actually-blocked chat — the dedicated blockConversation
        // event above is the only thing allowed to change this field.
        // `filters`, when passed, is { assigneeIdsFilter, unassignedFilter,
        // statusesFilter } for whichever list is being written — the sidebar's
        // agent/status dropdown state (see useWatiChatList/ChatSidebarColumn),
        // deliberately NOT the header's named filter tabs (see
        // matchesChatFilters' own comment for why those can't be replicated
        // here). A chat that doesn't match is dropped from the list if it was
        // there (its ticket moved out of the current filter) and never
        // inserted if it wasn't already visible.
        const upsertMapped = (setList, updatedChat, filters) => {
            setList(prev => {
                const exists = prev.some(c => c.id === updatedChat.id);
                if (filters && !matchesChatFilters({ ...updatedChat.raw?.ticket, starred: updatedChat.starred }, filters)) {
                    return exists ? prev.filter(c => c.id !== updatedChat.id) : prev;
                }
                const next = exists
                    ? prev.map(c => c.id === updatedChat.id
                        ? {
                            ...updatedChat, customer_type: c.customer_type, isBlocked: c.isBlocked,
                            tags: updatedChat.tags?.length ? updatedChat.tags : (c.tags || []),
                            listNotes: updatedChat.listNotes?.length ? updatedChat.listNotes : (c.listNotes || []),
                            // WATI's live conViewModel push doesn't reliably carry
                            // `username` the way the list-snapshot endpoint does
                            // (contactId/bsuid appear to always be present, username
                            // doesn't) — without this fallback, any live update on a
                            // hidden-number chat silently blanks an already-known
                            // username back to null before it's ever opened.
                            username: updatedChat.username || c.username || null,
                            contactId: updatedChat.contactId || c.contactId || null,
                            bsuid: updatedChat.bsuid || c.bsuid || null,
                        }
                        : c)
                    // A brand-new chat has no prior card to preserve
                    // customer_type from the way the `exists` branch above
                    // does — updatedChat.customer_type here is still
                    // mapChatItem's raw `item.type`, which for a live socket
                    // payload is WATI's *message* type ("text"/"image"/etc),
                    // never a real customer classification. null it out
                    // rather than let that string display as (or worse,
                    // silently coincide with) a customer type; the header's
                    // own profile fetch or the next full list refetch fills
                    // in the real value once known.
                    : [...prev, { ...updatedChat, customer_type: null }];
                return [...next].sort((a, b) => {
                    const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
                    const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
                    return bT - aT;
                });
            });
        };

        // Precompute the effective filters for each channel once per socket
        // batch — folds the header's "Assigned to me" tab and a
        // self-only sidebar agent selection into the same [myWatiId] shape
        // (see effectiveAssigneeIdsFilter) so matchesChatFilters only needs
        // to check assigneeIdsFilter membership.
        const myWatiId = authState?.user?.wati_id || null;
        const customerChatFilters = {
            assigneeIdsFilter: effectiveAssigneeIdsFilter({ chatFilters, selectedFilterIndex, assigneeIdsFilter, myWatiId }),
            unassignedFilter,
            statusesFilter,
            starredFilter: isFavouritesFilterTab((chatFilters || [])[selectedFilterIndex]),
        };
        const supplierChatFilters = {
            assigneeIdsFilter: effectiveAssigneeIdsFilter({
                chatFilters: supplierChat.chatFilters,
                selectedFilterIndex: supplierChat.selectedFilterIndex,
                assigneeIdsFilter: supplierChat.assigneeIdsFilter,
                myWatiId,
            }),
            unassignedFilter: supplierChat.unassignedFilter,
            statusesFilter: supplierChat.statusesFilter,
            starredFilter: isFavouritesFilterTab((supplierChat.chatFilters || [])[supplierChat.selectedFilterIndex]),
        };

        newUpdates.forEach(update => {
            // WATI hub protocol sends some events as arrays: [{type, target, arguments}]
            if (update.target === "catchSelectedConversation") {
                const chatId = update["0"];
                if (chatId) {
                    setChatlist(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
                    setQv4Chatlist(prev => prev.map(c => c.id === chatId ? { ...c, unreadCount: 0 } : c));
                    setActiveChats(prev => prev.map(c => c.id === chatId ? { ...c, unread_count: 0 } : c));
                    supplierChat.setActiveChats(prev => prev.map(c => c.id === chatId ? { ...c, unread_count: 0 } : c));
                }
                return;
            }

            // ── Mark as unread — fired when someone (possibly on another agent's
            // browser) explicitly marks a conversation unread from its last
            // received message. Payload: [{ conversationId, unreadCount }].
            // Deliberately a plain counter bump, not routed through
            // upsertMapped — the card's last_message_time doesn't change, so
            // it should stay exactly where it already sits in the list, not
            // jump to the top the way a new incoming message would.
            if (update.target === "MarkAsUnreadMessageResult") {
                const conversationId = update["0"]?.conversationId;
                if (!conversationId) return;
                const matches = c => c.conversationId === conversationId || c.id === conversationId;
                setChatlist(prev => prev.map(c => matches(c) ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c));
                setQv4Chatlist(prev => prev.map(c => matches(c) ? { ...c, unreadCount: (c.unreadCount || 0) + 1 } : c));
                setActiveChats(prev => prev.map(c => matches(c) ? { ...c, unread_count: (c.unread_count || 0) + 1 } : c));
                supplierChat.setActiveChats(prev => prev.map(c => matches(c) ? { ...c, unread_count: (c.unread_count || 0) + 1 } : c));
                return;
            }

            // ── Block / unblock — WATI's own moderation, not ours ────────────────────
            // Blocked chats never emit sendTextMessage/messageFromOperator over the
            // socket while blocked (no live message updates to worry about missing),
            // but block state itself changes live via this dedicated event:
            // [{ target: "blockConversation", arguments: [conversationId, isBlocked] }].
            if (update.target === "blockConversation") {
                const conversationId = update["0"];
                const nowBlocked = !!update["1"];
                if (!conversationId) return;
                // The event's argument is the ticket-level conversationId, not
                // necessarily the same value as the chat's own `id` (mapChatItem
                // keeps them as separate fields — see messageFromOperator's patch
                // just below, which matches the same way for the same reason).
                const matches = c => c.conversationId === conversationId || c.id === conversationId;
                const patchBlocked = prev => prev.map(c => matches(c) ? { ...c, isBlocked: nowBlocked } : c);

                setChatlist(patchBlocked);
                setQv4Chatlist(patchBlocked);
                setActiveChats(patchBlocked);
                supplierChat.setActiveChats(patchBlocked);

                // Only these two are mapChatItem-shaped (customer_name/phone) and
                // are what a "Go to chat" notification would actually navigate
                // to — chatlist/qv4Chatlist are just background badge/preload
                // lists, never rendered as their own drawer-able view. Read
                // straight from the closure rather than back out of a setState
                // updater — React doesn't guarantee invoking a functional
                // updater synchronously, so a `let` mutated inside one and read
                // immediately after (the previous version of this code) isn't
                // reliable; that's why the notification below silently never
                // fired. Same acceptable-staleness tradeoff `chatSearch` already
                // has elsewhere in this effect (only depends on [watiChatUpdates]).
                let foundChat = activeChats.find(matches);
                let channel = foundChat ? "customer" : null;
                if (!foundChat) {
                    foundChat = supplierChat.activeChats.find(matches);
                    channel = foundChat ? "supplier" : null;
                }

                // Not one of our loaded chats at all (e.g. a chat nobody here
                // has ever fetched) — nothing to update, nothing useful to tell
                // anyone. Skip entirely.
                if (!foundChat) return;

                // `foundChat` is the pre-patch object (read from the closure
                // before setActiveChats/supplierChat.setActiveChats above
                // actually applied — those don't mutate it in place), so it
                // still carries the OLD isBlocked. Anything handed forward
                // from here (specifically "Go to chat" below, which seeds the
                // drawer's own selected-chat snapshot directly rather than
                // deriving it live) needs the corrected value stamped on, or
                // the drawer opens showing the block state from before this
                // very event.
                foundChat = { ...foundChat, isBlocked: nowBlocked };

                const isCurrentlyOpen = channel === "supplier"
                    ? (activeChatIdRef.current === conversationId || activeChatIdRef.current === foundChat.id)
                    : (customerActiveChatIdRef.current === conversationId || customerActiveChatIdRef.current === foundChat.id);
                const statusWord = nowBlocked ? "blocked" : "unblocked";

                if (isCurrentlyOpen) {
                    // The chat is open right now — everything else (status/
                    // assignment/star controls, the Blocked chip, the compose-box
                    // warning banner) already follows automatically once isBlocked
                    // flips on the drawer's own selected-chat state (see
                    // QuerysetV4/Operations' matching page-local sync for that —
                    // this list patch alone doesn't reach a separate snapshot
                    // object). This notification is just a heads-up.
                    chatNotifApi.info({
      ...notificationDesign('customer', 'info'),
                        key: `chat-block-${conversationId}`,
                        message: nowBlocked ? "Chat blocked" : "Chat unblocked",
                        description: nowBlocked
                            ? "This conversation has been blocked — status, assignment, and starring are unavailable while blocked."
                            : "This conversation has been unblocked — status, assignment, and starring are available again.",
                        placement: "top",
                        duration: 6,
                    });
                } else {
                    // Loaded but not the chat currently open (drawer closed, or
                    // open on a different chat) — a quieter heads-up would be easy
                    // to miss, so this one names the customer, links straight to
                    // the chat, and stays until dismissed.
                    const nKey = `chat-block-sticky-${conversationId}-${Date.now()}`;
                    const name = foundChat.customer_name || foundChat.phone || "A chat";
                    chatNotifApi.info({
      ...notificationDesign('customer', 'info'),
                        key: nKey,
                        message: `Chat ${statusWord}`,
                        description: (
                            <div>
                                <div style={{ marginBottom: 6 }}>{name} has been {statusWord}.</div>
                                <Button
                                    size="small"
                                    type="link"
                                    style={{ padding: 0, height: "auto" }}
                                    onClick={() => {
                                        chatNotifApi.destroy(nKey);
                                        if (channel === "supplier") {
                                            navigate("/whatsapp_messages", { state: { openChatId: foundChat.id } });
                                        } else if (customerOpenChatRef.current) {
                                            customerOpenChatRef.current(foundChat);
                                        } else {
                                            // No customer chat page currently mounted, and there's
                                            // no location.state auto-open for it (unlike the
                                            // supplier page above) — best effort, lands on the
                                            // board but doesn't auto-select the chat.
                                            navigate("/querysetv4");
                                        }
                                    }}
                                >
                                    Go to chat →
                                </Button>
                            </div>
                        ),
                        placement: "top",
                        duration: 0,
                    });
                }
                return;
            }

            // Our own (operator-sent) messages broadcast on this event, not
            // "sendTextMessage" — that one's only ever seen for customer-sent
            // (incoming) messages. Without this, no outbound send ever updates
            // the card's preview/time. ChatPanelMessages.js already listens for
            // this same event (for the open conversation's bubbles) — same flat shape.
            if (update.target === "messageFromOperator") {
                const msg = update["0"];
                if (!msg?.conversationId) return;
                const isDoc = msg.type === "document";
                let lastMsg;
                if (msg.type === "audio") lastMsg = "🎤 Voice message";
                else if (msg.type === "image") lastMsg = `📷 ${msg.media?.caption || "Photo"}`;
                else if (msg.type === "video") lastMsg = `🎥 ${msg.media?.caption || "Video"}`;
                else if (isDoc) lastMsg = `📄 ${msg.media?.caption || "Document"}`;
                else if (msg.type === "sticker") lastMsg = "💌 Sticker";
                else lastMsg = msg.text;
                if (lastMsg == null) return;

                const msgTime = msg.time ? dayjs(parseFloat(msg.time) * 1000) : dayjs();
                try { sessionStorage.setItem(`wati_out_${msg.conversationId}`, JSON.stringify({ text: lastMsg, time: msgTime.valueOf() })); } catch {}
                const patch = prev => prev.map(c =>
                    (c.id === msg.conversationId || c.conversationId === msg.conversationId)
                        ? { ...c, last_message: lastMsg, last_message_time: msgTime }
                        : c
                );
                const resort = list => [...list].sort((a, b) => {
                    const aT = a.last_message_time?.isValid() ? a.last_message_time.valueOf() : 0;
                    const bT = b.last_message_time?.isValid() ? b.last_message_time.valueOf() : 0;
                    return bT - aT;
                });
                if (msg.channelId && msg.channelId === supplierChannelIdRef.current) {
                    supplierChat.setActiveChats(prev => resort(patch(prev)));
                } else if (!msg.channelId) {
                    setActiveChats(prev => resort(patch(prev)));
                }
                return;
            }

            // ── Ticket reassignment / topic / status change — customer channel only ──
            if (update.target === "updatingConversation") {
                const conversationId = update["0"];
                const ticket = update["1"];
                if (!conversationId || !ticket) return;
                // Reassignment/status-change can move a card outside the
                // currently applied agent/status/favourites filter — drop it
                // from the list rather than leave a stale, now-non-matching
                // card showing (see matchesChatFilters). This event's ticket
                // payload doesn't carry starred status, so stillMatches is
                // computed per-card below (inside the setter, where the
                // existing card's own `starred` is available) rather than
                // once outside it.
                setActiveChats(prev => {
                    const existing = prev.find(c => c.conversationId === conversationId);
                    const stillMatches = matchesChatFilters({ ...ticket, starred: existing?.starred }, customerChatFilters);
                    return prev
                        .map(c => {
                            if (c.conversationId !== conversationId) return c;
                            // When the window just reopened (status → Open, old expiry was in the past), reset expiry
                            const expiry = (ticket.status === 1 && c.expiry?.isValid() && c.expiry.isBefore(dayjs()))
                                ? dayjs().add(24, "hour")
                                : c.expiry;
                            return {
                                ...c,
                                ticket_status: ticket.status ?? null,
                                agent: ticket.assignedFullName || null,
                                topic: ticket.topicName || null,
                                expiry,
                                raw: c.raw ? { ...c.raw, ticket } : c.raw,
                            };
                        })
                        .filter(c => c.conversationId !== conversationId || stillMatches);
                });
                return;
            }

            // ── Broadcast message sent to a contact — customer channel only ──────────
            if (update.target === "updatingConversationByBroadcast") {
                const conv = update["0"];
                if (conv?.channelId !== null && conv?.channelId !== undefined) return;
                if (!conv?.id) return;
                const isOpen = customerActiveChatIdRef.current === conv.id;
                const updatedChat = mapChatItem({
                    id: conv.id,
                    name: conv.name,
                    lastUpdated: conv.lastUpdated,
                    lastMessageDate: conv.lastMessageDate,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    text: update["1"]?.finalText || conv.text,
                    messageType: "broadcast",
                    unreadCount: isOpen ? 0 : (conv.unreadCount || 0),
                    isStar: conv.isStar,
                    isBlocked: conv.isBlocked,
                    whatsappId: conv.whatsappId,
                    contactId: conv.contactId,
                    bsuid: conv.bsuid,
                    username: conv.username,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                    channelName: conv.channelName,
                    listNotes: conv.listNotes,
                });
                upsertMapped(setActiveChats, updatedChat, customerChatFilters);
                return;
            }

            if (
                update.target === "sendTextMessage" &&
                supplierChannelIdRef.current &&
                update["0"]?.message?.conViewModel?.channelId === supplierChannelIdRef.current
            ) {
                const conv = update["0"].message.conViewModel;
                const isOpen = activeChatIdRef.current === update["0"].conversationId;

                const updatedEntry = {
                    id: conv.id,
                    name: conv.name,
                    lastUpdated: conv.lastUpdated,
                    lastMessageDate: conv.lastMessageDate,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    text: conv.text,
                    lastMessageType: update["0"].messageType,
                    unreadCount: isOpen ? 0 : conv.unreadCount,
                    isStar: conv.isStar,
                    isBlocked: conv.isBlocked,
                    whatsappId: conv.whatsappId,
                    contactId: conv.contactId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                    channelName: conv.channelName,
                };

                setChatlist(prev => {
                    const exists = prev.some(c => c.id === updatedEntry.id);
                    // conv.type here is WATI's *message* type ("text"/"image"/etc), not
                    // our customer classification — never let it overwrite what the
                    // entry already had. Same for isBlocked — see upsertMapped's
                    // comment above; this event's payload isn't the source of truth
                    // for block state, blockConversation is.
                    const next = exists
                        ? prev.map(c => c.id === updatedEntry.id ? { ...updatedEntry, type: c.type, isBlocked: c.isBlocked } : c)
                        : [...prev, updatedEntry];
                    return [...next].sort((a, b) => parseFloat(b.lastUpdated) - parseFloat(a.lastUpdated));
                });

                // supplierChat.activeChats mirrors the same event onto the
                // supplier page's actual (filterable) list — a separate mapped
                // shape from the raw `chatlist` preload above, same as
                // customer's activeChats/qv4Chatlist split below.
                upsertMapped(supplierChat.setActiveChats, mapChatItem({
                    id: conv.id,
                    name: conv.name,
                    lastUpdated: conv.lastUpdated,
                    lastMessageDate: conv.lastMessageDate,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    text: conv.text || update["0"].message.text,
                    messageType: update["0"].messageType,
                    contacts: update["0"].message.contacts,
                    unreadCount: isOpen ? 0 : conv.unreadCount,
                    isStar: conv.isStar,
                    isBlocked: conv.isBlocked,
                    whatsappId: conv.whatsappId,
                    contactId: conv.contactId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                    channelName: conv.channelName,
                    listNotes: conv.listNotes,
                }), supplierChatFilters);

                if (update["0"].message.isOwner === false) {
                    const soundEnabled = loadSalesPreferences(authState?.id).soundEnabled;
                    if (soundEnabled) {
                        notificationAudioRef.current.currentTime = 0;
                        notificationAudioRef.current.play().catch(() => { });
                    }

                    const notifsEnabled = localStorage.getItem("wati_notifications_enabled") !== "false";
                    if (notifsEnabled && location.pathname !== "/whatsapp_messages") {
                        const nKey = `wati-notif-${Date.now()}`;
                        const userName = authState?.user?.name;
                        const convId = conv.id;
                        const msgId = update["0"].messageId;
                        const showCloseAll = activeNotifsRef.current.size >= 1;
                        const onCloseAll = () => {
                            activeNotifsRef.current.forEach(k => incomingNotifApi.destroy(k));
                            activeNotifsRef.current.clear();
                        };
                        activeNotifsRef.current.add(nKey);
                        const placement = localStorage.getItem("wati_notification_position") || "bottomLeft";
                        incomingNotifApi.info({
      ...notificationDesign('customer', 'info'),
                            key: nKey,
                            message: <span style={{ fontSize: 13, fontWeight: 600 }}>{conv.name}</span>,
                            description: (
                                <>
                                    <div style={{ fontSize: 12, color: "#555", marginBottom: 4, lineHeight: 1.4 }}>
                                        💬 {conv.text || "(media)"}
                                    </div>
                                    <QuickReplyBox
                                        phone={conv.whatsappId}
                                        userName={userName}
                                        notificationKey={nKey}
                                        channelId={supplierChannelIdRef.current}
                                        showCloseAll={showCloseAll}
                                        onCloseAll={onCloseAll}
                                        notification={incomingNotifApi}
                                        onNavigate={() => {
                                            incomingNotifApi.destroy(nKey);
                                            navigate("/whatsapp_messages", { state: { openChatId: convId, openMessageId: msgId } });
                                        }}
                                    />
                                </>
                            ),
                            placement,
                            duration: 8,
                            style: { width: 300 },
                            onClose: () => activeNotifsRef.current.delete(nKey),
                        });
                    }
                }
            }

            // QV4 channel (channelId === null) — track unread count + play sound
            if (update.target === "sendTextMessage" && update["0"]?.message?.conViewModel?.channelId === null) {
                const conv = update["0"].message.conViewModel;
                const fullEntry = {
                    id: conv.id,
                    name: conv.name,
                    text: conv.text,
                    messageType: update["0"].messageType,
                    lastUpdated: conv.lastUpdated,
                    lastIncomingDate: conv.lastIncomingDate,
                    type: conv.type,
                    unreadCount: conv.unreadCount || 0,
                    isStar: conv.isStar,
                    whatsappId: conv.whatsappId,
                    activeTicketType: conv.activeTicketType,
                    ticket: conv.ticket,
                    replyDeadline: conv.replyDeadline,
                };
                setQv4Chatlist(prev => {
                    const exists = prev.some(c => c.id === conv.id);
                    if (!exists) return [...prev, fullEntry];
                    // conv.type here is WATI's *message* type ("text"/"image"/etc), not
                    // our customer classification — never let it overwrite what the
                    // entry already had.
                    return prev.map(c => c.id === conv.id ? { ...c, ...fullEntry, type: c.type } : c);
                });
                if (update["0"].message.isOwner === false) {
                    const soundEnabled = loadSalesPreferences(authState?.id).soundEnabled;
                    if (soundEnabled) {
                        notificationAudioRef.current.currentTime = 0;
                        notificationAudioRef.current.play().catch(() => {});
                    }
                }

                // Mirror onto the customer page's actual (filterable) list too.
                // Focused Chat Mode (focusedChatIds non-null) takes priority over
                // the plain-search guard below: only the pasted-in chats it holds
                // keep receiving live patches, every other incoming update is
                // dropped so it can't smuggle an unrelated chat back into the
                // frozen list. Absent that, skip entirely while an active text
                // search is in progress, so a live update can't silently
                // reorder/replace what's currently on screen out from under
                // someone mid-search (matches the guard QuerysetV4's own copy of
                // this logic used to apply locally).
                const focusedActive = !!focusedChatIds;
                if (focusedActive ? focusedChatIds.has(conv.id) : !chatSearch.trim()) {
                    const isOpen = customerActiveChatIdRef.current === conv.id;
                    upsertMapped(setActiveChats, mapChatItem({
                        id: conv.id,
                        name: conv.name,
                        lastUpdated: conv.lastUpdated,
                        lastMessageDate: conv.lastMessageDate,
                        lastIncomingDate: conv.lastIncomingDate,
                        type: conv.type,
                        text: conv.text || update["0"].message.text,
                        messageType: update["0"].messageType,
                        contacts: update["0"].message.contacts,
                        unreadCount: isOpen ? 0 : conv.unreadCount,
                        isStar: conv.isStar,
                        isBlocked: conv.isBlocked,
                        whatsappId: conv.whatsappId,
                        contactId: conv.contactId,
                        bsuid: conv.bsuid,
                        username: conv.username,
                        activeTicketType: conv.activeTicketType,
                        ticket: conv.ticket,
                        replyDeadline: conv.replyDeadline,
                        channelName: conv.channelName,
                        tags: conv.tags,
                        listNotes: conv.listNotes,
                    }), customerChatFilters);
                }
            }
        });
    }, [watiChatUpdates, watiAuthBroken]);

    const totalUnread = chatlist.filter(c => c.unreadCount > 0).length;
    const qv4TotalUnread = qv4Chatlist.filter(c => (c.unreadCount || 0) > 0).length;

    return (
        <WatiChatContext.Provider value={{
            chatlist,
            setChatlist,
            chatlistLoading,
            setChatlistLoading,
            continuationToken,
            setContinuationToken,
            chatlistCount,
            getAllChats,
            supplierChannelId,
            activeChatIdRef,
            customerActiveChatIdRef,
            customerOpenChatRef,
            totalUnread,
            qv4TotalUnread,
            qv4Chatlist,
            qv4ChatsLoading,
            qv4ChatlistToken,
            qv4ChatlistCount,
            activeChats, setActiveChats,
            chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
            chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
            chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
            assigneeIdsFilter, onSelectAgentFilter, commitAgentFilter,
            statusesFilter, onSelectStatusFilter, commitStatusFilter, clearSidebarFilters,
            unassignedFilter, setUnassignedFilter,
            dateRangeFilter, setDateRangeFilter, dateScanStatus, continueDateScan, cancelDateScan,
            setChatContinuationToken, chatContinuationTokenRef,
            buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter,
            focusedChatIds, focusedLoading, focusedProgress, loadFocusedChats, exitFocusedChatMode,
            reconnectSignal,
            watiAuthBroken, retryWatiAuth,
            // The supplier page's (WhatsappMessages) own chat list — same shape
            // as the flat customer values above, grouped under one key since
            // WhatsappMessages already consumed it this way when it built its
            // own instance locally; grouping keeps that call site's usage
            // (supplierChat.activeChats, supplierChat.setActiveChats, etc.)
            // unchanged now that the instance itself lives here instead.
            supplierChat,
            notification: chatNotifApi,
        }}>
            {children}
            {/* Each notification.useNotification() instance's holder element carries
                the same hardcoded internal key ("notification-holder", set by antd
                itself) — rendering two side by side as direct siblings here made React
                see two children with that same key. Wrapping each in its own
                uniquely-keyed Fragment moves the collision one level down, where it's
                no longer being compared against a sibling. */}
            <React.Fragment key="chat-notif-holder">{chatNotifContextHolder}</React.Fragment>
            <React.Fragment key="incoming-notif-holder">{incomingNotifContextHolder}</React.Fragment>
        </WatiChatContext.Provider>
    );
};
